import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NumberGrid } from '../components/NumberGrid';
import { DjKeypad } from '../components/DjKeypad';
import { PlayedList } from '../components/PlayedList';
import { SongBook } from '../components/SongBook';
import { PackageSelector } from '../components/PackageSelector';
import { GameSettings } from '../components/GameSettings';
import { BlanksGenerator } from '../components/BlanksGenerator';
import { AdminPanel } from '../components/AdminPanel';
import { AdminLogin } from '../components/AdminLogin';
import { useSessionSync } from '../hooks/useSessionSync';
import { useScreenWakeLock } from '../hooks/useScreenWakeLock';
import { useSessionStore } from '../store/sessionStore';
import { useAdminStore } from '../store/adminStore';
import { checkAuth } from '../services/authService';
import { clearSavedRole } from '../services/roleStorage';
import './DjPage.css';

/** Пульт диджея: клавиатура, сетка, список сыгранных и управление. */
export const DjPage = () => {
  useSessionSync('dj');
  useScreenWakeLock();
  const navigate = useNavigate();
  const session = useSessionStore((state) => state.session);
  const playNumber = useSessionStore((state) => state.playNumber);
  const stopPlayback = useSessionStore((state) => state.stopPlayback);
  const dontStop = useSessionStore((state) => state.dontStop);
  const resumePaused = useSessionStore((state) => state.resumePaused);
  const setHidePlayed = useSessionStore((state) => state.setHidePlayed);
  const resetGame = useSessionStore((state) => state.resetGame);
  const endGame = useSessionStore((state) => state.endGame);
  const { packages, loadPackages } = useAdminStore();

  const [typed, setTyped] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSongs, setShowSongs] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const previous = meta?.getAttribute('content') || '';
    meta?.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
    );
    return () => {
      meta?.setAttribute('content', previous);
    };
  }, []);

  useEffect(() => {
    if (!showAdmin) return;
    checkAuth().then(setIsAuthenticated);
  }, [showAdmin]);

  const total = Object.keys(session.numberToTrackMap).length;
  const allNumbers = Array.from({ length: total }, (_, i) => i + 1);
  const numbers = session.hidePlayed
    ? allNumbers.filter((number) => !session.playedNumbers.includes(number))
    : allNumbers;

  const playedItems = session.playedNumbers.map((number) => ({
    number,
    track: session.numberToTrackMap[String(number)],
  }));

  const handlePlayTyped = async () => {
    const number = parseInt(typed, 10);
    if (!Number.isInteger(number)) return;
    setError('');
    try {
      await playNumber(number);
      setTyped('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить');
    }
  };

  const handlePlayNumber = async (number: number) => {
    setError('');
    try {
      await playNumber(number);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить');
    }
  };

  const switchRole = () => {
    clearSavedRole();
    navigate('/');
  };

  return (
    <div className="dj-page">
      <header className="dj-header">
        <div>
          <p className="dj-kicker">Пульт диджея</p>
          <h1 className="dj-title">{session.package?.name || 'Музыкальное лото'}</h1>
        </div>
        <div className="dj-header-actions">
          {session.package && <BlanksGenerator />}
          <GameSettings />
          <button type="button" className="dj-chip" onClick={() => setShowAdmin((v) => !v)}>
            {showAdmin ? 'К игре' : 'Админ'}
          </button>
          {session.package && (
            <>
              <button type="button" className="dj-chip" onClick={() => resetGame()}>
                Сбросить
              </button>
              <button type="button" className="dj-chip" onClick={() => endGame()}>
                Новый пакет
              </button>
            </>
          )}
          <button type="button" className="dj-chip" onClick={switchRole}>
            Сменить роль
          </button>
        </div>
      </header>

      {!session.displayConnected && (
        <div className="dj-banner">Телевизор не в сети — звука не будет</div>
      )}
      {error && <div className="dj-banner dj-banner-error">{error}</div>}

      {showAdmin ? (
        isAuthenticated === false ? (
          <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />
        ) : isAuthenticated === true ? (
          <AdminPanel />
        ) : (
          <p className="dj-waiting">Проверка доступа...</p>
        )
      ) : !session.package ? (
        <PackageSelector packages={packages} />
      ) : (
        <div className="dj-layout">
          <div className="dj-main">
            <div className="dj-now">
              {session.isPlaying && session.currentTrack ? (
                <>
                  <span className="dj-now-label">Сейчас</span>
                  #{session.currentTrack.number} {session.currentTrack.artist} — {session.currentTrack.title}
                </>
              ) : session.pausedTrack ? (
                <>
                  <span className="dj-now-label">На паузе</span>
                  #{session.pausedTrack.number} {session.pausedTrack.artist} — {session.pausedTrack.title}
                </>
              ) : (
                'Выберите номер'
              )}
            </div>

            <DjKeypad
              value={typed}
              onChange={setTyped}
              onPlay={handlePlayTyped}
              disabled={session.isPlaying}
            />

            <div className="dj-controls">
              <button
                type="button"
                className="dj-action"
                disabled={!session.isPlaying}
                onClick={() => stopPlayback()}
              >
                Стоп
              </button>
              <button
                type="button"
                className="dj-action"
                disabled={!session.isPlaying || session.isContinued}
                onClick={() => dontStop()}
              >
                Не останавливать
              </button>
              <button
                type="button"
                className="dj-action dj-action-accent"
                disabled={session.isPlaying || !session.pausedTrack}
                onClick={() => resumePaused()}
              >
                Дослушать предыдущую
              </button>
              <button
                type="button"
                className={`dj-action ${session.hidePlayed ? 'dj-action-on' : ''}`}
                onClick={() => setHidePlayed(!session.hidePlayed)}
              >
                {session.hidePlayed ? 'Показать сыгранное' : 'Скрыть сыгранное'}
              </button>
              <button
                type="button"
                className={`dj-action ${showSongs ? 'dj-action-on' : ''}`}
                onClick={() => setShowSongs(true)}
              >
                Посмотреть песни
              </button>
            </div>
          </div>

          <NumberGrid
            variant="compact"
            numbers={numbers}
            playedNumbers={session.playedNumbers}
            disabled={session.isPlaying}
            onSelect={handlePlayNumber}
          />

          <PlayedList items={playedItems} />
          <SongBook
            open={showSongs}
            onClose={() => setShowSongs(false)}
            numberToTrackMap={session.numberToTrackMap}
            playedNumbers={session.playedNumbers}
          />
        </div>
      )}
    </div>
  );
};
